<script setup lang="ts">
/* eslint-disable max-lines */
import MiiaFwConfig from '@/configs/config'
import { getAllTileUrls } from '@/libs/leaflet/tileManager'
import {
  calcRectangularArea,
  getDownloadedDistrictId,
  getAllDownloadedFeature,
  type CustomFeature
} from '@/libs/leaflet/featureManager'
import { useOfflineDataDownloadProcessStore } from '@/stores/offlineDataDownloadProcessStore'
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import DataDownloadProgressModal from '@/components/setting/offline/DataDownloadProgressModal.vue'
import DownloadMapManager from '@/components/setting/offline/DownloadMapManager.vue'
import ToggleSwitchUtil from '@/components/common/toggle/ToggleSwitchUtil.vue'
import L from 'leaflet'
import {
  BModal,
  BCard,
  BContainer,
  BDropdown,
  BDropdownItem,
  BButton,
  BRow,
  BCol
} from 'bootstrap-vue-next'
import { useCommonModalStore } from '@/stores/commonModalStore'
import CommonModal from '@/components/common/dialog/CommonModal.vue'

const offlineDataDownloadProcessStore = useOfflineDataDownloadProcessStore()
const commonModalStore = useCommonModalStore()
const dataDownloadProgressModalRef = ref<InstanceType<typeof DataDownloadProgressModal> | null>(
  null
)
const downloadMapManagerRef = ref<InstanceType<typeof DownloadMapManager> | null>(null)
const map = ref<L.Map | null>(null as unknown as L.Map)
const showModal = ref<boolean>(false)
// ダウンロード対象の地域
const selectedDownloadFeature = ref<CustomFeature[]>([])
const isSelectAllFeature = ref<boolean>(true)
const isChecked = ref<boolean>(true)
const cityName = ref<string>('')
const downloadedDistrictIds = ref<string[]>([])
// 初期選択同一フラグ
const isInitSelectState = computed(() => {
  const currentSelectedDistrictIds: string[] = []
  for (const d of selectedDownloadFeature.value) {
    currentSelectedDistrictIds.push(d.properties.districtId)
  }
  return currentSelectedDistrictIds.toString() === downloadedDistrictIds.value.toString()
})

/**
 * 大容量データダウンロード警告ダイアログ
 * @param callBack 「はい」選択時に実行するCallback
 * @returns なし
 */
const largeDataDownloadWarningDialog = (callBack: { (): void; (): void; (): void }) => {
  commonModalStore
    .openConfirmModal({
      title: 'ダウンロード警告',
      body: '大容量のデータをダウンロードしようとしています。<br>以下の注意事項をご確認ください。<br><br>・一度に大容量のデータをダウンロードする場合、長時間がかかったり動作が不安定になる可能性があります。<br>町丁目の選択数を減らし、複数回に分けてダウンロードすることを推奨しています。<br>・ストレージが不足している場合、ダウンロードに失敗する可能性があります。<br>事前にストレージの状態をご確認ください。<br><br>本当に実行しますか?',
      okTitle: 'はい',
      cancelTitle: 'いいえ'
    })
    .then(() => {
      callBack()
    })
}

/**
 * ダウンロード実行ハンドラー
 * @param なし
 * @returns なし
 */
const executeDownLoadMapHandler = async () => {
  downloadedDistrictIds.value = await getDownloadedDistrictId()
  const targetFeatures = selectedDownloadFeature.value.filter(
    (f: { properties: { districtId: string } }) =>
      !downloadedDistrictIds.value.includes(f.properties.districtId)
  )
  offlineDataDownloadProcessStore.updateDownloadFeature(targetFeatures)
  const amount = await calcDataAmount(targetFeatures)

  const execute = () => {
    dataDownloadProgressModalRef.value!.openModal()
    closeModal()
    dataDownloadProgressModalRef.value!.executeDownLoadMapEntity().finally(async () => {
      // ダウンロード後に再度更新
      downloadedDistrictIds.value = (await getDownloadedDistrictId()).sort()
    })
  }

  commonModalStore
    .openConfirmModal({
      title: 'オフライン地図データをダウンロードします',
      body: ' ダウンロードを実行します<br>よろしいですか?',
      okTitle: 'はい',
      cancelTitle: 'いいえ'
    })
    .then(() => {
      // 一度のダウンロードで推奨されている地図画像数を超えている場合、警告を表示
      if (amount > MiiaFwConfig.config.disasterEmergency.recommendedOfflineMapDownloadCount) {
        largeDataDownloadWarningDialog(execute)
      } else {
        execute()
      }
    })
}

/**
 * ダウンロード実行ハンドラー(更新処理)
 * @param なし
 * @returns なし
 */
const executeUpdateDownLoadedMapHandler = async () => {
  const res = await getAllDownloadedFeature()
  offlineDataDownloadProcessStore.updateDownloadFeature(res)
  const amount = await calcDataAmount(res)
  const execute = () => {
    closeModal()
    // 選択状態を初期状態に戻す
    downloadMapManagerRef.value!.resetAllDistrict()
    dataDownloadProgressModalRef.value!.openModal()
    dataDownloadProgressModalRef.value!.executeDownLoadMapEntity(true).finally(async () => {
      // ダウンロード後に再度更新
      downloadedDistrictIds.value = (await getDownloadedDistrictId()).sort()
    })
  }
  commonModalStore
    .openConfirmModal({
      title: 'オフライン地図データを更新します',
      body: 'ダウンロード済みの地図を更新します。<br>よろしいですか?<br><br>※更新前の地図は削除されます。<br>更新をキャンセルしても更新前には戻らないためご注意ください。',
      okTitle: '更新',
      cancelTitle: 'キャンセル'
    })
    .then(() => {
      // 一度のダウンロードで推奨されている地図画像数を超えている場合、警告を表示
      if (amount > MiiaFwConfig.config.disasterEmergency.recommendedOfflineMapDownloadCount) {
        largeDataDownloadWarningDialog(execute)
      } else {
        execute()
      }
    })
}

/**
 * ダウンロード対象の地図タイル数を計算
 * @param features ダウンロード対象の地図
 * @returns ダウンロード対象の地図タイル数
 */
const calcDataAmount = async (features: CustomFeature[]): Promise<number> => {
  // ダウンロード対象の地区を取得
  const allTileCount = (() => {
    const data: string[] = []
    for (const f of features) {
      const area = calcRectangularArea(f)
      if (downloadMapManagerRef.value?.leaflet.map == null) {
        continue
      }
      const tileUrls = getAllTileUrls(
        downloadMapManagerRef.value.leaflet.map as L.Map,
        L.latLng(area.swCoordinates[1], area.swCoordinates[0]),
        L.latLng(area.neCoordinates[1], area.neCoordinates[0]),
        18
      )
      data.push(...tileUrls)
    }
    // 地図タイルが複数地区で重複する場合があるので、単一となるようにフィルター
    const targetDownLoadTiles = Array.from(new Set(data))
    return targetDownLoadTiles.length
  })()
  return allTileCount
}

/**
 * ダウンロード実行ハンドラー
 * @param なし
 * @returns なし
 */
const openDeleteModal = () => {
  commonModalStore
    .openConfirmModal({
      title: 'オフライン地図データを削除します',
      body: 'ダウンロード済みの地図データを全て削除します。<br>よろしいですか?',
      okTitle: '削除',
      cancelTitle: 'キャンセル'
    })
    .then(() => {
      dataDownloadProgressModalRef.value!.clearIndexedDB().then(async () => {
        downloadMapManagerRef.value!.resetAllDistrict(true)
        selectedDownloadFeature.value = []
        offlineDataDownloadProcessStore.updateDownloadFeature(selectedDownloadFeature.value)
        downloadMapManagerRef.value!.resetSelectedFeature()
        downloadedDistrictIds.value = (await getDownloadedDistrictId()).sort()
        // 再度全選択処理を実行
        if (!isChecked.value) {
          downloadMapManagerRef.value!.selectAllDistrict()
          offlineDataDownloadProcessStore.updateIsSelectAllFeature(true)
        }
        window.localStorage.setItem('availableOfflineMap', '0')
        commonModalStore
          .openAlertModal({
            body: 'オフライン地図データを削除しました。',
            title: '削除が完了しました',
            okTitle: '閉じる'
          })
          .then(() => {
            offlineDataDownloadProcessStore.updateIsDownloaded(false)
          })
      })
    })
}

/**
 * Mapコンポーネントから選択済みの区域を取得
 * @param features 選択された区域のリスト
 * @returns なし
 */
const setSelectedFeature = (features: CustomFeature[]) => {
  selectedDownloadFeature.value = features
}

/**
 * トグルスイッチの状態を取得
 * @param newValue 新しい状態
 * @returns なし
 */
const updateChecked = (newValue: boolean) => {
  isChecked.value = newValue
}

/**
 * コンポーネントのメイン画面を開く
 * @param なし
 * @returns なし
 */
const openModal = () => {
  showModal.value = true
  updateChecked(true)
  nextTick(async () => {
    // DOMの更新後に呼び出し
    downloadMapManagerRef
      .value!.createMap()
      .then(() => {
        map.value = downloadMapManagerRef.value!.leaflet.map
        cityName.value = downloadMapManagerRef.value!.cityName
      })
      .catch(() => {
        // マップ生成でエラーになるのであればモーダルを閉じる
        closeModal()
      })
  })
}

/**
 * コンポーネントのメイン画面を閉じる
 * @param なし
 * @returns なし
 */
const closeModal = () => {
  showModal.value = false
}

// トグルスイッチオンオフ時のイベント
watch(isChecked, newValue => {
  if (!newValue) {
    downloadMapManagerRef.value!.selectAllDistrict()
  } else {
    downloadMapManagerRef.value!.resetAllDistrict()
  }
  offlineDataDownloadProcessStore.updateIsSelectAllFeature(!newValue)
})
// ライフサイクル
onMounted(async () => {
  downloadedDistrictIds.value = (await getDownloadedDistrictId()).sort()
})
onBeforeUnmount(() => {})
defineExpose({
  showModal,
  isSelectAllFeature,
  isChecked,
  cityName,
  targetDownloadFeature: selectedDownloadFeature,
  downloadedDistrictIds,
  isInitSelectState,
  executeUpdateDownLoadedMapHandler,
  openModal,
  executeDownLoadMapHandler,
  setSelectedFeature,
  closeModal,
  updateChecked,
  openDeleteModal
})
</script>

<template>
  <div>
    <BModal
      no-close-on-backdrop
      centered
      stacking
      v-model="showModal"
      ref="modal"
      dialog-class="download-map-dialog"
      :no-enforce-focus="true"
    >
      <!-- ヘッダー部 -->
      <template #header>
        <div class="w-100">
          <div class="d-flex flex-row bd-highlight justify-content-center">
            <p class="h2 mt-3">オフライン地図の設定</p>
          </div>
        </div>
      </template>
      <BCard class="m-0 border-0" :style="{ height: '700px' }">
        <BContainer>
          <BRow class="justify-content-between align-items-center">
            <!-- Toggle Switch -->
            <BCol>
              <ToggleSwitchUtil
                ref="ToggleSwitch"
                class="pl-3 fs-4"
                :checked="isChecked"
                :label="`町丁目を選択する (OFFの場合は${cityName}全体をダウンロードします)`"
                @valueChange="updateChecked"
              />
            </BCol>
            <!-- Dropdown Menu -->
            <BCol cols="auto">
              <BDropdown size="sm" no-caret>
                <!-- ボタン部分 -->
                <template #button-content>
                  <span class="vertical-dots"></span>
                </template>
                <!-- メニュー項目 -->
                <BDropdownItem
                  @click="executeUpdateDownLoadedMapHandler"
                  :disabled="downloadedDistrictIds.length === 0"
                  class="fs-4"
                >
                  更新
                </BDropdownItem>
                <BDropdownItem
                  @click="openDeleteModal"
                  :disabled="downloadedDistrictIds.length === 0"
                  class="fs-4"
                >
                  削除
                </BDropdownItem>
              </BDropdown>
            </BCol>
          </BRow>
          <BRow>
            <BCard class="m-1" :style="{ width: '100%', height: '640px' }">
              <DownloadMapManager
                ref="downloadMapManagerRef"
                isSelectAllFeature="isSelectAllFeature"
                @selectedFeature="setSelectedFeature"
              />
            </BCard>
          </BRow>
        </BContainer>
      </BCard>
      <!-- フッター部 -->
      <template #footer>
        <div class="w-100">
          <div class="d-flex flex-row justify-content-end">
            <!-- 閉じるボタン -->
            <BButton
              size="lg"
              class="mx-2 download-close-button fs-4"
              variant="secondary"
              @click="closeModal"
            >
              閉じる
            </BButton>
            <!-- ダウンロードボタン -->
            <BButton
              size="lg"
              class="mx-2 fs-4"
              variant="info"
              :disabled="selectedDownloadFeature.length === 0 || isInitSelectState"
              @click="executeDownLoadMapHandler"
            >
              ダウンロード
            </BButton>
          </div>
        </div>
      </template>
    </BModal>
    <CustomButtonDialog ref="customBtnDialogRef" />
    <DataDownloadProgressModal ref="dataDownloadProgressModalRef" :map="map as L.Map" />
    <CommonModal />
  </div>
</template>

<style scoped>
.vertical-dots {
  display: inline-block;
  width: 4px;
  height: 16px;
  background-image: radial-gradient(circle, currentColor 1px, transparent 1px);
  background-size: 100% 33.33%;
}

/* ダイアログのフルスクリーン表示用 */
:deep(.dropdown-toggle) {
  background-color: #ffffff;
  color: #000000;
  border: none;
}

:deep(.dropdown-toggle:hover) {
  background-color: #cfcfcf;
  color: #000000;
  border: none;
}
</style>
